import { ColumnDef, Row } from "@tanstack/react-table";
import Tag from "@/components/Tag";
import type { TagType, TagStyle, TagTooltip } from "@/components/Tag/Tag";
import { SortFieldType } from "./sortUtils";
import type { MaterialNames } from "@/components/material_symbol_names";

export interface TagExtras {
  iconName?: MaterialNames;
  tooltipText?: string;
  tooltip?: TagTooltip;
}

export interface BuildTagColumnProps<T> {
  accessorKey: string;
  header: string;
  getTagLabel: (row: T) => string | null | undefined;
  getTagType: (row: T) => TagType;
  getTagStyle?: (row: T) => TagStyle;
  getIconName?: (row: T) => MaterialNames | undefined;
  getTooltipText?: (row: T) => string | undefined;
  getTooltip?: (row: T) => TagTooltip | undefined;
  /** Return iconName + tooltipText together so the computation runs once per cell. Takes precedence over getIconName / getTooltipText. */
  getTagExtras?: (row: T) => TagExtras;
  tagSize?: "small" | "medium" | "large";
  /** When true, status pills of different label lengths share a consistent min-width. Defaults to false so non-status columns are not widened. */
  equalWidth?: boolean;
  size?: number;
  minSize?: number;
  enableSorting?: boolean;
  sortType?: SortFieldType;
  meta?: Record<string, any>;
}

export function buildTagColumn<T>({
  accessorKey,
  header,
  getTagLabel,
  getTagType,
  getTagStyle,
  getIconName,
  getTooltipText,
  getTooltip,
  getTagExtras,
  tagSize = "small",
  equalWidth = false,
  size,
  minSize,
  enableSorting = false,
  sortType,
  meta
}: BuildTagColumnProps<T>): ColumnDef<T> {
  const CellComponent = ({ row }: { row: Row<T> }) => {
    const label = getTagLabel(row.original);

    if (!label) {
      return <span className="opacity-20">--</span>;
    }

    const tagType = getTagType(row.original);
    const tagStyle = getTagStyle?.(row.original);
    const extras = getTagExtras?.(row.original);
    const iconName = extras?.iconName ?? getIconName?.(row.original);
    const tooltip = extras?.tooltip ?? getTooltip?.(row.original);
    const tooltipText = extras?.tooltipText ?? getTooltipText?.(row.original);

    return (
      <Tag
        tagType={tagType}
        size={tagSize}
        equalWidth={equalWidth}
        {...(tagStyle && { tagStyle })}
        {...(iconName && { iconName })}
        {...(tooltip && { tooltip })}
        {...(!tooltip && tooltipText && { tooltipText })}
      >
        {label}
      </Tag>
    );
  };

  return {
    accessorKey,
    header,
    ...(size && { size }),
    ...(minSize && { minSize }),
    enableSorting,
    meta: {
      headerClassName: "hide-header-in-column",
      ...(sortType && { sortType }),
      ...meta
    },
    cell: CellComponent
  };
}
